import React, { useEffect, useState } from "react";
import { X, ExternalLink, ShoppingBag } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { imageUrl } from "@/lib/api";

/**
 * PromoPopup — auto-show popup banner dari /api/popups/active?location=...
 * Props:
 *  - location: "homepage" | "after_login" | "checkout"
 *
 * Hidden saat:
 *  - rute /admin/*
 *  - rute /pesanan/* (proses pembayaran / checkout final)
 *  - rute /login, /register, /forgot-password, /reset-password (auth pages)
 */
const HIDDEN_PREFIXES = ["/admin", "/pesanan", "/login", "/register", "/forgot-password", "/reset-password"];

function useIsMobile() {
  const [mobile, setMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 640 : false));
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return mobile;
}

export default function PromoPopup({ location = "homepage" }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [popup, setPopup] = useState(null);
  const [closed, setClosed] = useState(false);

  const blocked = HIDDEN_PREFIXES.some((p) => pathname.startsWith(p));

  useEffect(() => {
    if (blocked) return;
    let alive = true;
    api.get(`/popups/active?location=${location}`)
      .then((r) => { if (alive && r.data && r.data.id) setPopup(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [location, pathname, blocked]);

  if (!popup || closed || blocked) return null;

  const markSeen = () => {
    api.post(`/popups/${popup.id}/seen`).catch(() => {});
  };
  const close = () => {
    setClosed(true);
    markSeen();
  };
  const onCta = () => {
    markSeen();
    setClosed(true);
    if (popup.cta_link) {
      if (popup.cta_link.startsWith("http")) {
        window.open(popup.cta_link, "_blank", "noopener,noreferrer");
      } else {
        navigate(popup.cta_link);
      }
    }
  };

  // Pilih gambar: mobile → image_url_mobile (fallback ke image_url), desktop → image_url
  const img = isMobile ? (popup.image_url_mobile || popup.image_url) : popup.image_url;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 animate-fadeIn"
      data-testid="promo-popup"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <button
          onClick={close}
          className="absolute top-2 right-2 z-10 w-8 h-8 grid place-items-center rounded-full bg-white/90 hover:bg-white shadow"
          data-testid="popup-close-btn"
          aria-label="Tutup"
        >
          <X size={18} />
        </button>
        {img && (
          <img
            src={imageUrl(img)}
            alt={popup.title}
            className="w-full h-48 sm:h-64 object-cover"
            data-testid={isMobile ? "popup-image-mobile" : "popup-image-desktop"}
          />
        )}
        <div className="p-5 space-y-3">
          <h3 className="font-display text-xl font-black" data-testid="popup-title">{popup.title}</h3>
          {popup.description && <p className="text-sm text-[#5C5C5C] whitespace-pre-line">{popup.description}</p>}
          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            <button
              onClick={onCta}
              className="flex-1 brand-btn-primary text-white font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors"
              data-testid="popup-cta-btn"
            >
              <ShoppingBag size={16} />
              {popup.cta_text || "Belanja Sekarang"}
              {popup.cta_link && popup.cta_link.startsWith("http") && <ExternalLink size={14} />}
            </button>
            <button
              onClick={close}
              className="sm:flex-none bg-[#FAF9F6] hover:bg-[#F0E5C0] text-[#5C5C5C] font-semibold py-3 px-5 rounded-xl transition-colors"
              data-testid="popup-close-secondary-btn"
            >
              Tutup
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
